import {
  AnimatedSpinner,
  Button,
  Dialog,
  Divider,
  IconAdjust,
  IconButton,
  LayoutVerticalSpacer,
} from '@madpaws/design-system';
import { useFormikContext } from 'formik';
import { useState } from 'react';

import {
  ABOUT_THE_SITTER_OPTIONS,
  DEFAULT_PRICE_RANGE,
  SITTER_REVIEWS_ANY_ID,
  SITTER_REVIEWS_OPTIONS,
} from '~/common/constants/search';
import { trackEvent } from '~/components/analytics/analytics';
import { UPDATE_FILTER_SITTERS } from '~/components/analytics/constants';
import { DOM_CONTAINER_ID } from '~/components/constants';
import { getEventAllProperties } from '~/components/pages/SearchResults/tracking/getUpdatedSearchTrackingProperties';

import styles from './AdvancedFiltersMainDialog.module.css';
import { getChangedFilterCount } from './utils';
import { CheckboxGroupField } from '../../ui/CheckboxGroupField/CheckboxGroupField';
import { RadioGroupField } from '../../ui/RadioGroupField/RadioGroupField';
import { SitterHomeOptionsFields } from '../../ui/SitterHomeOptionsFields/SitterHomeOptionsFields';
import { SliderField } from '../../ui/SliderField/SliderField';

import type { ReactElement } from 'react';
import type { SearchFilters } from '~/common/types/search';

type Props = {
  handleAdvancedFiltersSubmit: () => void;
  isLoading: boolean;
  isPriceSliderShown?: boolean;
  isReviewFilterEnabled?: boolean;
};

const AdvancedFiltersMainDialog = ({
  handleAdvancedFiltersSubmit,
  isLoading,
  isPriceSliderShown,
  isReviewFilterEnabled,
}: Props): ReactElement => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { setFieldValue, initialValues, values } = useFormikContext<SearchFilters>();

  const changedFilterCount = getChangedFilterCount(values.advancedFilters);

  const getFilterButtonLabel = (): string => {
    if (changedFilterCount === 0) {
      return 'Filter';
    }

    return `Filter (${changedFilterCount})`;
  };

  const handleAdvancedFiltersClear = (): void => {
    setFieldValue('advancedFilters', {
      price: DEFAULT_PRICE_RANGE,
      sittersHome: [],
      sittersPetsKids: [],
      aboutSitter: [],
      reviewsMin: SITTER_REVIEWS_ANY_ID,
    });
  };

  const handleAdvancedFiltersReset = (): void => {
    const price = initialValues.advancedFilters?.price ?? DEFAULT_PRICE_RANGE;
    const sittersHome = initialValues.advancedFilters?.sittersHome ?? [];
    const sittersPetsKids = initialValues.advancedFilters?.sittersPetsKids ?? [];
    const aboutSitter = initialValues.advancedFilters?.aboutSitter ?? [];
    const reviewsMin = initialValues.advancedFilters?.reviewsMin ?? SITTER_REVIEWS_ANY_ID;

    setFieldValue('advancedFilters', {
      price,
      sittersHome,
      sittersPetsKids,
      aboutSitter,
      reviewsMin,
    });
  };

  const handleFiltersDialogToggle = (): void => {
    setIsDialogOpen(!isDialogOpen);
  };

  const handleDialogDismiss = (): void => {
    handleAdvancedFiltersReset();
    handleFiltersDialogToggle();
  };

  const handleFiltersDialogSearch = (): void => {
    const eventAllProperties = getEventAllProperties(values);

    trackEvent(UPDATE_FILTER_SITTERS, eventAllProperties);

    handleFiltersDialogToggle();
    handleAdvancedFiltersSubmit();
  };

  return (
    <div className={styles.root}>
      <div className={`${styles.filterButton} ${changedFilterCount > 0 && styles.changed}`}>
        <Button
          iconLeading={<IconAdjust />}
          isDisabled={isLoading}
          isFullBleed
          label={getFilterButtonLabel()}
          onClick={handleFiltersDialogToggle}
          size="small1x"
          variant="tertiary"
        />
      </div>
      <div className={styles.filterIconButton}>
        <IconButton
          isDisabled={isLoading}
          label="Filter"
          onClick={handleFiltersDialogToggle}
          variant="tertiary"
        >
          {isLoading ? <AnimatedSpinner /> : <IconAdjust />}
        </IconButton>
      </div>
      <Dialog
        cta={{
          size: 'large1x',
          label: 'Show Sitters',
          onClick: handleFiltersDialogSearch,
          isDisabled: isLoading,
        }}
        domContainerId={DOM_CONTAINER_ID}
        heading="Filters"
        isOpen={isDialogOpen}
        onRequestClose={handleDialogDismiss}
        secondaryAction={{
          label: 'Clear All',
          onClick: handleAdvancedFiltersClear,
        }}
      >
        <LayoutVerticalSpacer space="large1x">
          {isPriceSliderShown ? (
            <>
              <SliderField max={300} name="advancedFilters.price" />
              <Divider />
            </>
          ) : (
            <></>
          )}
          <>
            {isReviewFilterEnabled && (
              <>
                <RadioGroupField
                  legend="Sitter reviews"
                  name="advancedFilters.reviewsMin"
                  options={SITTER_REVIEWS_OPTIONS}
                  variant="tiled"
                />
                <Divider />
              </>
            )}
          </>
          <SitterHomeOptionsFields />
          <CheckboxGroupField
            legend="About the Sitter"
            name="advancedFilters.aboutSitter"
            options={ABOUT_THE_SITTER_OPTIONS}
            variant="buttonMultiselect"
          />
        </LayoutVerticalSpacer>
      </Dialog>
    </div>
  );
};

export { AdvancedFiltersMainDialog };
